// Schema-keyed metadata fields, on the schema engine. A metadata field whose KEY
// is an `ipfs://<cid>` URL declares that its value should conform to that
// schema. The schema-engine port of the v1 buildSchemaKeyRoot/collectSchemaKeyCids/
// schemaKeyCid (blob-schema-edit.ts). We synthesize an OPEN map: the
// schema-keyed keys are validated against their (inlined) schemas; every other
// key is accepted (`values: {}` — the empty schema imposes no constraint).
import {documentMetadataSchema} from '@seed-hypermedia/client/schema-resolve'
import {parseCidString} from '../dag-json'
import {fieldsToProperties, STRUCT_URL, type HypermediaSchema} from './engine'

const DAG_CBOR_CODE = 0x71

/** The bare DAG-CBOR CID of a schema-keyed field key (`ipfs://<cid>`), or null. */
export function schemaKeyCid(key: string): string | null {
  if (typeof key !== 'string' || !key.startsWith('ipfs://')) return null
  const cid = key.replace(/^ipfs:\/\//, '').split('/')[0] ?? ''
  return parseCidString(cid)?.code === DAG_CBOR_CODE ? cid : null
}

/** The distinct DAG-CBOR CIDs referenced by the schema-keyed keys in `keys`. */
export function collectSchemaKeyCids(keys: string[]): string[] {
  const cids: string[] = []
  for (const key of keys) {
    const cid = schemaKeyCid(key)
    if (cid && !cids.includes(cid)) cids.push(cid)
  }
  return cids
}

/**
 * An open map schema for a value whose schema-keyed (`ipfs://<cid>`) keys
 * should validate against their schemas. Returns undefined when no key is
 * schema-keyed (no schema-awareness needed). `byCid` supplies the resolved
 * schemas (see useSchemaRegistry).
 */
export function buildSchemaKeyRoot(
  keys: string[],
  byCid: Record<string, HypermediaSchema>,
): HypermediaSchema | undefined {
  const properties: Record<string, HypermediaSchema> = {}
  for (const key of keys) {
    const cid = schemaKeyCid(key)
    if (cid && byCid[cid]) properties[key] = byCid[cid]!
  }
  if (Object.keys(properties).length === 0) return undefined
  return {
    type: STRUCT_URL,
    properties: fieldsToProperties(
      Object.entries(properties).map(([name, schema]) => ({name, schema, required: false})),
    ),
    values: {},
  }
}

/**
 * The effective schema for a document's metadata — base document metadata extended by the
 * document's type, kept open. Lives in the client package so the CLI and agents check
 * documents exactly as this editor does.
 */
export {documentMetadataSchema}
